import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useFocusItems } from './useFocusItems';
import { useAppState } from './useAppState';
import { useFocusCompletion } from './useFocusCompletion';
import { useTransactionReflection } from './useTransactionReflection';
import { usePageView } from '../../hooks/usePageView';
import { useConnectedAccountsProperty } from '../../hooks/useConnectedAccountsProperty';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useTimezoneSync } from '../../hooks/useTimezoneSync';
import { useOnboardingSync } from './useOnboardingSync';
import { ViewRouter } from './ViewRouter';
import { DayProvider } from '../../context/DayContext';
import { FocusItemsProvider } from '../../context/FocusItemsContext';
import { useAuth } from '../../context/AuthContext';
import { useGoalCelebrations } from '../../hooks/useGoalCelebrations';
import { GoalCelebrationProvider } from '../../context/GoalCelebrationContext';
import { GoalCelebrationModal } from '../../components/goals/GoalCelebrationModal';
import { ReviewPromptProvider } from '../../context/ReviewPromptContext';
import { useOnboardingRedirect } from '../../hooks/useOnboardingRedirect';
import { useBootGateTelemetry } from '../../hooks/useBootGateTelemetry';

export const AuthenticatedApp: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Navigation hooks
    usePageView();
    useConnectedAccountsProperty();
    useScrollToTop();
    useTimezoneSync(currentUser?.uid ?? null);
    useOnboardingSync();

    // Redirect new users to /welcome. The Plaid OAuth return page is exempt:
    // it finishes a bank handshake mid-onboarding and sends the person back to
    // /welcome itself (CONNECT_FLOW_PLAN.md §7 G).
    const needsOnboarding = useOnboardingRedirect(currentUser?.uid);
    useEffect(() => {
        if (needsOnboarding === true && location.pathname !== '/welcome' && location.pathname !== '/oauth-return') {
            navigate('/welcome', { replace: true });
        }
    }, [needsOnboarding, location.pathname, navigate]);

    // 1. Focus items hook (must run before app state)
    const {
        focusItems,
        focusHasDocs,
        focusHasStaleContent,
        focusLoading,
        contentRefreshing,
        contentSettled
    } = useFocusItems();

    // 2. Transaction reflection hook (creates handler for app state)
    const { updateTransactionReflection: updateTransactionReflectionHandler } = useTransactionReflection({
        setPendingReflections: () => {} // Will be replaced after useAppState
    });

    // 3. App state hook (depends on focus items)
    const {
        contextState: preliminaryContextState,
        accounts,
        wellnessScore,
        setWellnessScore,
        mindfulMinutes,
        setMindfulMinutes,
        focusCards,
        setFocusCards,
        transactions,
        setPendingReflections
    } = useAppState({
        focusItems,
        focusHasDocs,
        focusLoading,
        contentRefreshing,
        contentSettled,
        markFocusCardComplete: () => {}, // Will be replaced by focus completion hook
        updateTransactionReflection: updateTransactionReflectionHandler,
        connectBank: async () => {} // Will be replaced by focus completion hook
    });

    // 4. Transaction reflection hook with correct setPendingReflections
    const { updateTransactionReflection } = useTransactionReflection({
        setPendingReflections,
        uid: currentUser?.uid
    });

    // 5. Focus completion hook (depends on focusCards, focusItems)
    const {
        markFocusCardComplete,
        connectBank,
        isPaymentModalOpen,
        setIsPaymentModalOpen,
        handlePaymentComplete
    } = useFocusCompletion({
        focusCards,
        setFocusCards,
        focusItems,
        setWellnessScore,
        setMindfulMinutes
    });

    // 6. Build final context state with actual handlers
    const contextState = {
        ...preliminaryContextState,
        markFocusCardComplete,
        updateTransactionReflection,
        connectBank
    };

    // Show loading spinner while data is loading or onboarding state is unknown —
    // EXCEPT on /welcome. The onboarding flow consumes no DataContext data up
    // front, so blocking it on the global data/onboarding gate just delays
    // the first screen. Let it render immediately; data finishes loading in the
    // background. Other routes still wait so they don't flash empty or pre-redirect.
    const onOnboarding = location.pathname === '/welcome';
    const bootGateBlocked = !onOnboarding && (contextState.isLoading || needsOnboarding === null);
    // Telemetry only (GA4 boot_data_ready + Sentry boot_gate_stall) — measures
    // how long this gate holds; see useBootGateTelemetry for why.
    useBootGateTelemetry(bootGateBlocked);
    if (bootGateBlocked) {
        return (
            <div className="h-screen w-full flex flex-col items-center justify-center bg-sand">
                <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
                <p className="mt-4 text-growth font-serif italic animate-pulse">Breathing...</p>
            </div>
        );
    }

    return (
        <DayProvider>
            {/* Re-publishes the ONE focus subscription above so surfaces outside
                the daily practice (e.g. the Story north star) can read what the
                practice is already asking for, without a second listener. */}
            <FocusItemsProvider focusItems={focusItems} focusLoading={focusLoading}>
                <AuthenticatedAppInner
                    contextState={contextState}
                    isPaymentModalOpen={isPaymentModalOpen}
                    setIsPaymentModalOpen={setIsPaymentModalOpen}
                    handlePaymentComplete={handlePaymentComplete}
                />
            </FocusItemsProvider>
        </DayProvider>
    );
};

/**
 * Inner component that lives inside DayProvider so useGoalCelebrations
 * gets a stable localDate from context (not a new string on every render).
 */
const AuthenticatedAppInner: React.FC<{
    contextState: any;
    isPaymentModalOpen: boolean;
    setIsPaymentModalOpen: (open: boolean) => void;
    handlePaymentComplete: () => void;
}> = ({ contextState, isPaymentModalOpen, setIsPaymentModalOpen, handlePaymentComplete }) => {
    const { celebrations, isOpen: celebrationOpen, close: closeCelebration, markSeen, replay, isReplay } = useGoalCelebrations();

    // Only a *successful* (non-replay) celebration is a positive moment to ride.
    const celebrationSuccess = !isReplay && celebrations.some(c => c.success);

    return (
        <GoalCelebrationProvider replay={replay}>
            <ReviewPromptProvider celebrationOpen={celebrationOpen} celebrationSuccess={celebrationSuccess}>
                <ViewRouter
                    contextState={contextState}
                    isPaymentModalOpen={isPaymentModalOpen}
                    setIsPaymentModalOpen={setIsPaymentModalOpen}
                    handlePaymentComplete={handlePaymentComplete}
                />
            </ReviewPromptProvider>
            <GoalCelebrationModal
                celebrations={celebrations}
                isOpen={celebrationOpen}
                onClose={closeCelebration}
                onMarkSeen={markSeen}
                isReplay={isReplay}
            />
        </GoalCelebrationProvider>
    );
};
